<template>
    <section id="about" class="relative py-20 md:py-32 bg-soft-bg overflow-hidden">
        <div class="absolute top-1/2 right-0 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-blue-5 blur-3xl" />
        <div class="absolute top-1/2 left-0 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-cyan-5 blur-3xl" />

        <div class="relative mx-auto max-w-7xl px-5 sm:px-8">
            <div class="relative rounded-[2.5rem] md:rounded-[3rem] overflow-hidden bg-gradient-to-br from-[#0d1f1c] via-[#0a1a17] to-[#091815] border border-white-5 shadow-[0_40px_80px_-30px_rgba(11,18,32,0.4)]">
                <div class="absolute top-1/4 right-[10%] h-3 w-3 rounded-full bg-[#ff5a4f] shadow-[0_0_40px_15px_rgba(255,90,79,0.5)]" />
                <div class="absolute top-2/3 right-[20%] h-2 w-2 rounded-full bg-[#ff7a6f] shadow-[0_0_30px_10px_rgba(255,122,111,0.4)]" />
                <div class="absolute bottom-1/4 right-[5%] h-1.5 w-1.5 rounded-full bg-[#F26B5A] shadow-[0_0_25px_8px_rgba(6,182,212,0.4)]" />
                <div class="absolute inset-0 opacity-[0.04]" :style="{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '28px 28px' }" />

                <div class="relative grid lg:grid-cols-12 gap-0 items-stretch">
                    <div class="lg:col-span-5 relative min-h-[420px] md:min-h-[560px] lg:min-h-[640px]">
                        <img
                            :src="portraitImage"
                            alt="محمد غنيم — مؤسس البرمجة مع غنيم"
                            loading="lazy"
                            width="1300"
                            height="1300"
                            class="absolute inset-0 w-full h-full object-cover object-center"
                        />
                        <div class="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-[#091815] via-[rgba(9,24,21,0.6)] to-transparent hidden lg:block" />
                        <div class="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#091815] via-[rgba(9,24,21,0.4)] to-transparent lg:hidden" />

                        <div class="absolute bottom-5 right-5 md:bottom-7 md:right-7 z-10 rounded-2xl bg-white-95 backdrop-blur px-5 py-3 shadow-[var(--shadow-glow)]">
                            <div class="text-[10px] font-bold tracking-widest text-brand-navy-60">FOUNDER</div>
                            <div class="font-extrabold text-brand-navy">محمد غنيم</div>
                        </div>
                    </div>

                    <div class="lg:col-span-7 relative p-7 sm:p-10 md:p-14 lg:p-16 flex flex-col justify-center text-white">
                        <h2 class="welcome-about-title">
                            <span class="welcome-about-title__line">مين هو</span>
                            <span class="welcome-about-title__name">محمد غنيم؟</span>
                        </h2>

                        <p class="mt-6 md:mt-8 text-base md:text-lg text-white-80 leading-[1.85] md:leading-[1.9] max-w-xl">
                            رائد أعمال مصري شغّال على تقاطع التعليم، التكنولوجيا، والذكاء الاصطناعي — هدفه يبني منظومة تساعد الطلاب يفهموا التكنولوجيا ويستعدوا للمستقبل.
                        </p>

                        <ul class="mt-6 space-y-4 max-w-xl">
                            <li v-for="item in aboutCompanies" :key="item.brand" class="flex items-start gap-3">
                                <span class="mt-2 h-1.5 w-1.5 rounded-full bg-brand-cyan flex-shrink-0" />
                                <div class="flex-1">
                                    <div class="text-sm md:text-base">
                                        <span class="text-white-60">{{ item.role }}</span>{{ ' ' }}
                                        <span class="text-white font-semibold">{{ item.brand }}</span>
                                    </div>
                                    <p class="mt-1.5 text-xs md:text-sm text-white-65 leading-[1.75] md:leading-[1.8]">{{ item.desc }}</p>
                                </div>
                            </li>
                        </ul>

                        <div class="mt-8 grid grid-cols-3 gap-3 md:gap-5 max-w-xl">
                            <div
                                v-for="s in aboutStats"
                                :key="s.label"
                                class="rounded-2xl bg-[rgba(255,255,255,0.06)] border border-white-10 backdrop-blur px-3 py-4 md:px-5 md:py-5"
                            >
                                <div class="text-2xl md:text-3xl font-extrabold text-white">{{ s.value }}</div>
                                <div class="mt-1 text-[11px] md:text-xs text-white-60 leading-tight">{{ s.label }}</div>
                            </div>
                        </div>

                        <div class="mt-7 flex flex-wrap gap-2">
                            <span
                                v-for="t in aboutTags"
                                :key="t"
                                class="rounded-full bg-white-8 border border-white-15 px-3.5 py-1.5 text-xs font-semibold text-white-85"
                            >{{ t }}</span>
                        </div>

                        <div class="mt-8 flex flex-wrap gap-3">
                            <a :href="LINKTREE" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 rounded-full bg-white text-brand-navy px-6 py-3 font-bold hover:bg-brand-cyan hover:text-white transition-colors">
                                اعرف أكثر عن غنيم <lucide-icon name="ArrowLeft" class="h-4 w-4" />
                            </a>
                            <router-link :to="{ name: 'courses' }" class="inline-flex items-center gap-2 rounded-full bg-white-10 border border-white-20 text-white px-6 py-3 font-bold hover:bg-white-15 transition-colors">
                                شوف الكورسات
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import LucideIcon from './LucideIcon.vue';
import { aboutStats, aboutCompanies, aboutTags, LINKTREE } from './landing-data';

export default {
    name: 'WelcomeAbout',
    components: { LucideIcon },
    data() {
        return {
            portraitImage: require('@/assets/welcome/ghonaim-portrait.webp'),
            aboutStats,
            aboutCompanies,
            aboutTags,
            LINKTREE,
        };
    },
};
</script>

<style lang="scss" scoped>
.welcome-about-title {
    font-weight: 800;
    line-height: 1.5;
    color: #fff;

    &__line {
        display: block;
        font-size: clamp(1.65rem, 3.8vw, 2.35rem);
        line-height: 1.45;
        margin-bottom: 0.35rem;
    }

    &__name {
        display: block;
        font-size: clamp(1.85rem, 4.5vw, 2.75rem);
        line-height: 1.55;
        padding-bottom: 0.12em;
        color: #f26b5a;
        overflow: visible;
    }
}
</style>
